// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { number, t } from "../i18n";

// Approving a new device by typing the code it shows. The field waits to be
// asked for: the request always starts on the new device, and a code field in
// plain view invites typing in a code someone else sent.
export function NewDeviceCard() {
  const navigate = useNavigate();
  const [entering, setEntering] = useState(false);
  const [code, setCode] = useState("");
  const start = useRef<HTMLButtonElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const cancelled = useRef(false);
  // The button that opened the field gives way to it, and takes focus back.
  useEffect(() => {
    if (entering) field.current?.focus();
    else if (cancelled.current) start.current?.focus();
    cancelled.current = false;
  }, [entering]);
  function submit(e: FormEvent) {
    e.preventDefault();
    void navigate({
      to: "/approve/$signInCode",
      params: { signInCode: code.trim() },
      search: true,
    });
  }
  function cancel() {
    cancelled.current = true;
    setCode("");
    setEntering(false);
  }
  const steps = [
    t("افتح {host} على الجهاز الجديد", { host: location.host }),
    t("اختر تسجيل الدخول، ثم الدخول بهاتفك"),
    t("امسح رمز QR بهذا الجهاز، أو أدخل رمزه هنا"),
  ];
  return (
    <section className="account-card new-device-card">
      <h2>{t("الدخول من جهاز جديد")}</h2>
      {entering ? (
        <>
          <p>{t("أدخل الرمز الظاهر على الهاتف أو الحاسوب الآخر.")}</p>
          <form onSubmit={submit}>
            <label className="device-code-box">
              <span>{t("رمز الجهاز")}</span>
              <input
                ref={field}
                dir="ltr"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="ABCD-EFGH"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                required
                minLength={8}
                maxLength={30}
              />
            </label>
            <div className="new-device-actions">
              <button className="button">{t("متابعة")}</button>
              <button
                type="button"
                className="button secondary"
                onClick={cancel}
              >
                {t("إلغاء")}
              </button>
            </div>
          </form>
        </>
      ) : (
        <>
          <p>
            {t(
              "اسمح لهاتف أو حاسوب آخر بالدخول إلى حسابك. بلا كلمة مرور، ولا شيء لتتذكره.",
            )}
          </p>
          <ol className="device-steps">
            {steps.map((step, index) => (
              <li key={index}>
                <span aria-hidden="true">{number(index + 1)}</span>
                {step}
              </li>
            ))}
          </ol>
          <button
            ref={start}
            className="button new-device-start"
            onClick={() => setEntering(true)}
          >
            {t("إدخال رمز الجهاز")}
          </button>
        </>
      )}
    </section>
  );
}
